import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Star, CheckSquare } from 'lucide-react';
import { get, post } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { PointBalanceBanner, ChoreCard, RewardCard, LedgerTable } from '@/widgets/index';
import { Navbar } from '@/widgets/Navbar';
import { ProgressBar, Card } from '@/shared/ui';
import type { Chore, ChoreCompletion } from '@/entities/chore';
import type { Reward } from '@/entities/reward';
import type { LedgerTransaction, PointBalance } from '@/entities/ledger';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
interface RewardsResponse {
  rewards: Reward[];
}

interface TransactionsResponse {
  transactions: LedgerTransaction[];
}

interface BalanceResponse {
  balance: PointBalance;
}

// ---------------------------------------------------------------------------
// ChildDashboardPage
// ---------------------------------------------------------------------------
export default function ChildDashboardPage(): React.ReactElement {
  const { appUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const childId = appUser?.uid ?? '';
  const familyId = appUser?.familyId ?? '';

  // Data queries
  const { data: balanceData } = useQuery<PointBalance>({
    queryKey: ['balance', familyId, childId],
    queryFn: () =>
      get<BalanceResponse>(`/families/${familyId}/balances/${childId}`).then((r) => r.balance),
    enabled: Boolean(familyId && childId),
  });

  // Use /chores/today — returns schedule-filtered chores + today's completions for this child
  const { data: todayData, isLoading: choresLoading } = useQuery<{ chores: Chore[]; completions: ChoreCompletion[] }>({
    queryKey: ['chores-today', familyId, childId],
    queryFn: () => get<{ chores: Chore[]; completions: ChoreCompletion[] }>(`/families/${familyId}/chores/today`),
    enabled: Boolean(familyId && childId),
  });

  const { data: rewardsData, isLoading: rewardsLoading } = useQuery<RewardsResponse>({
    queryKey: ['rewards', familyId],
    queryFn: () => get<RewardsResponse>(`/families/${familyId}/rewards?active=true`),
    enabled: Boolean(familyId),
  });

  const { data: txData, isLoading: txLoading } = useQuery<TransactionsResponse>({
    queryKey: ['ledger', familyId, childId, 'recent'],
    queryFn: () => get<TransactionsResponse>(`/families/${familyId}/ledger?childId=${childId}&limit=5`),
    enabled: Boolean(familyId && childId),
  });

  // Mark chore complete mutation
  const markCompleteMutation = useMutation({
    mutationFn: (choreId: string) =>
      post<void>(`/families/${familyId}/chores/${choreId}/complete`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['completions', familyId] });
    },
  });

  // Redeem reward mutation
  const redeemMutation = useMutation({
    mutationFn: (rewardId: string) =>
      post<void>(`/families/${familyId}/rewards/${rewardId}/redeem`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['redemptions', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['balance', familyId] });
    },
  });

  const [confirmRewardId, setConfirmRewardId] = React.useState<string | null>(null);

  const todayChores = todayData?.chores ?? [];
  const todayCompletions = todayData?.completions ?? [];
  const rewards = rewardsData?.rewards ?? [];
  const transactions = txData?.transactions ?? [];
  const availablePoints = balanceData?.availablePoints ?? 0;

  // Find the nearest affordable reward for the progress bar
  const sortedByDiff = [...rewards].sort(
    (a, b) => Math.abs(a.pointCost - availablePoints) - Math.abs(b.pointCost - availablePoints),
  );
  const nearestReward = sortedByDiff.find((r) => r.pointCost > availablePoints);
  const progressToNearest = nearestReward
    ? Math.min(100, Math.round((availablePoints / nearestReward.pointCost) * 100))
    : 100;

  const getChoreCompletions = (choreId: string): ChoreCompletion[] =>
    todayCompletions.filter((c) => c.choreId === choreId);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-lg px-4 py-6 space-y-6">
        {/* Point Balance Banner */}
        <PointBalanceBanner
          childId={childId}
          familyId={familyId}
          childName={appUser?.displayName ?? ''}
        />

        {/* Progress to nearest reward */}
        {nearestReward && (
          <Card className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Star size={16} className="text-accent-500" aria-hidden="true" />
              <p className="text-sm font-semibold text-slate-800">
                {availablePoints >= nearestReward.pointCost
                  ? `You can afford ${nearestReward.name}!`
                  : `You need ${(nearestReward.pointCost - availablePoints).toLocaleString()} more pts for ${nearestReward.name}`}
              </p>
            </div>
            <ProgressBar
              value={progressToNearest}
              colorScheme="secondary"
              label={nearestReward.emoji ? `${nearestReward.emoji} ${nearestReward.name}` : nearestReward.name}
            />
          </Card>
        )}

        {/* Today's Chores */}
        <section aria-label="Today's chores">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <CheckSquare size={18} className="text-primary-600" aria-hidden="true" />
              Today's Chores
            </h2>
            <span className="text-xs text-slate-500">
              {todayCompletions.filter((c) => c.status === 'approved').length}/{todayChores.length} done
            </span>
          </div>

          {choresLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse h-32">{null}</Card>
              ))}
            </div>
          ) : todayChores.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 py-8 text-center">
              <span className="text-4xl" aria-hidden="true">🎉</span>
              <p className="text-sm text-slate-600 font-medium">All done! No chores today.</p>
              <p className="text-xs text-slate-400">Check back tomorrow.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {todayChores.map((chore) => (
                <ChoreCard
                  key={chore.id}
                  chore={chore}
                  completions={getChoreCompletions(chore.id)}
                  userRole="child"
                  onMarkComplete={() => markCompleteMutation.mutate(chore.id)}
                  onApprove={() => undefined}
                  onReject={() => undefined}
                />
              ))}
            </div>
          )}
        </section>

        {/* Reward preview */}
        <section aria-label="My rewards">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-slate-900">
              My Rewards
            </h2>
            <button
              type="button"
              onClick={() => navigate('/rewards')}
              className="text-xs text-primary-600 hover:text-primary-800 font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
            >
              See all →
            </button>
          </div>

          {rewardsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse h-40">{null}</Card>
              ))}
            </div>
          ) : rewards.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 py-6 text-center">
              <span className="text-3xl" aria-hidden="true">🎁</span>
              <p className="text-sm text-slate-500">No rewards yet. Ask a parent to add some!</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {rewards.slice(0, 3).map((reward) => (
                <RewardCard
                  key={reward.id}
                  reward={reward}
                  availablePoints={availablePoints}
                  userRole="child"
                  onRedeem={() => setConfirmRewardId(reward.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Recent activity */}
        <section aria-label="Recent activity">
          <h2 className="mb-3 text-base font-bold text-slate-900">Recent Activity</h2>
          <LedgerTable
            transactions={transactions}
            isLoading={txLoading}
            emptyMessage="No activity yet — complete some chores to earn points!"
          />
        </section>
      </main>

      {/* Redeem confirmation overlay */}
      {confirmRewardId && (() => {
        const reward = rewards.find((r) => r.id === confirmRewardId);
        if (!reward) return null;
        return (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setConfirmRewardId(null)}
            aria-modal="true"
            role="dialog"
            aria-label={`Redeem ${reward.name}`}
          >
            <div
              className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl space-y-4 animate-[slideUp_150ms_ease-out]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="text-5xl mb-2" aria-hidden="true">{reward.emoji ?? '🎁'}</div>
                <h3 className="text-lg font-bold text-slate-900">{reward.name}</h3>
                <p className="text-sm text-slate-500 mt-1">
                  This will cost <span className="font-semibold text-slate-700">{reward.pointCost.toLocaleString()} pts</span>
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => {
                    redeemMutation.mutate(confirmRewardId);
                    setConfirmRewardId(null);
                  }}
                  className="h-12 w-full rounded-xl bg-secondary-600 text-white font-semibold text-base hover:bg-secondary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2 transition-colors duration-150"
                >
                  Yes, redeem it!
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRewardId(null)}
                  className="h-11 w-full rounded-xl text-slate-600 font-medium text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
